import type { NavigationGuard, Route } from 'vue-router'
import type { ContextProvider } from '../providers'
import { CONTAINER } from '../config'
import { TYPES } from '../config/inversify.types'
import { SetContextWithUrlGuard } from './set-context-with-url.guard'

/**
 * Creates a Vue Router navigation guard to handle external routes based on their names
 * Redirects to external routes using window.location with context information
 *
 * @param externalRoutes - List of route names that should be redirected externally
 * @returns NavigationGuard function to use with router.beforeEach
 */
export function createNavigationGuard(externalRoutes: string[]): NavigationGuard {
  return (to: Route, _from: Route, next): void => {
    // Check if the route should be redirected externally
    if (to.name && externalRoutes.includes(to.name)) {
      const context = CONTAINER.get<ContextProvider>(TYPES.ContextProvider)()
      const contextRoute = SetContextWithUrlGuard.updateRouteWithContext(to, context)
      const url = new URL(contextRoute.path, window.location.origin)

      if (contextRoute.query && !['landing', 'explainSearch'].includes(to.name)) {
        Object.entries(contextRoute.query).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            url.searchParams.set(key, String(value))
          }
        })
      }

      window.location.href = url.href
      return
    }

    // Continue with normal navigation for internal routes
    next()
  }
}
